import { type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AiChat from 'components/AiChat/AiChat';
import Button from 'components/Button/Button';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import Modal from 'components/Modal/Modal';
import { BUTTON_VARIANT } from 'constants/interfaces';

import type IAiChatScreen from 'components/AiChat/interfaces/IAiChatScreen';

import styles from './scss/TargetedMarketing.module.scss';

interface IWatchAiChatDialog {
  open: boolean,
  title: string,
  description: string,
  nestChatId: number | null,
  screen: IAiChatScreen,
  contextSlot: ReactNode,
  extraSlot: ReactNode,
  sendRef?: { current: ((text: string) => Promise<number | null>) | null },
  onClose: () => void,
  onChatCreated: (aiChatId: number) => void,
}

function WatchAiChatDialog({
  open,
  title,
  description,
  nestChatId,
  screen,
  contextSlot,
  extraSlot,
  sendRef = undefined,
  onClose,
  onChatCreated,
}: IWatchAiChatDialog): ReactElement {
  const { t } = useTranslation();

  const handleCreated = (createdChatId: number | null) => {
    if (!createdChatId) {
      toast.error(t('targetedMarketing.chatBindFailed'));
      return;
    }
    onChatCreated(createdChatId);
  };

  const renderFailed = (retry: () => void) => (
    <div className={styles.chatFailed} role="alert">
      <p>{t('aiChat.panelFailed')}</p>
      <Button variant={BUTTON_VARIANT.secondary} onClick={retry} title={t('aiChat.tryAgain')} />
    </div>
  );

  return (
    <Modal
      open={open}
      title={title}
      className={styles.modalWide}
      description={description}
      onClose={onClose}
      isButtonsVisible={false}
    >
      {contextSlot}
      <div className={styles.aiChatHost}>
        {open ? (
          <ErrorBoundary
            key={nestChatId ?? 'new'}
            fallback={renderFailed}
          >
            <AiChat
              chatId={nestChatId || undefined}
              screen={screen}
              sendRef={sendRef}
              onChatCreated={handleCreated}
              className={styles.aiChat}
            />
          </ErrorBoundary>
        ) : null}
      </div>
      {extraSlot}
    </Modal>
  );
}

WatchAiChatDialog.defaultProps = {
  sendRef: undefined,
};

export default WatchAiChatDialog;
